import { isString } from 'lodash';
import React, {
  ChangeEvent,
  KeyboardEvent,
  startTransition,
  useEffect,
  useRef,
  useState,
} from 'react';
import { InputTextLabel } from 'src/components/input/Input.styled';

import { WidgetsCommonProps } from '../widget.interface';
import { InputBox, Wrapper } from './NumberInputWidget.styled';

export const NumberInputWidget = React.forwardRef(
  (props: WidgetsCommonProps, ref) => {
    const { value, error, hasError, options, disabled } = props;

    const { min, max, suffix, prefix, step, size, integer } = options || {};
    const [inputValue, setInputValue] = useState(value ?? '');
    const userEditedRef = useRef(false);

    // Update value from parent when value is passed
    useEffect(() => {
      if (
        userEditedRef.current &&
        (inputValue === undefined || inputValue === '') &&
        value !== undefined
      ) {
        return;
      }
      userEditedRef.current = false;
      setInputValue(value ?? '');
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const updateValue = (newValue: number | undefined) => {
      if (newValue === undefined) {
        return props.onChange(undefined, true);
      } else {
        const formattedValue =
          prefix !== undefined || suffix !== undefined
            ? `${prefix || ''}${newValue}${suffix || ''}`
            : newValue;
        props.onChange(formattedValue, true);
      }
    };

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (integer && ['.', ',', 'e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault();
      }
    };

    const sanitizeIntegerInput = (raw: string) =>
      raw.replace(/\D/g, '').replace(/^0+/, '');

    const onChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
      userEditedRef.current = true;
      if (integer) {
        const sanitizedValue = sanitizeIntegerInput(target.value);
        if (sanitizedValue !== target.value) {
          target.value = sanitizedValue;
        }
      }

      const newValue = target.value ? Number(target.value) : undefined;

      setInputValue(integer ? newValue ?? '' : newValue);

      props.options?.useImmediateUpdate
        ? updateValue(newValue)
        : startTransition(() => {
            updateValue(newValue);
          });
    };

    const formatInputValue = (value: number) => {
      if (suffix) {
        return String(value).replace(suffix, '');
      }
      if (prefix) {
        return String(value).replace(prefix, '');
      }

      return value;
    };

    return (
      <Wrapper>
        <InputTextLabel component={isString(props.label) ? undefined : 'span'}>
          {props.label}
        </InputTextLabel>
        <InputBox
          type="number"
          value={formatInputValue(inputValue)}
          placeholder={props?.options?.placeholder}
          onChange={onChange}
          onKeyDown={onKeyDown}
          disabled={disabled}
          error={hasError}
          helperText={hasError ? error : undefined}
          inputProps={{
            min,
            max,
            step,
          }}
          size={size}
          inputRef={ref}
        />
      </Wrapper>
    );
  },
);
